import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { apiFetch } from "../lib/api";

const EMPTY = { name: "", status: "applying", description: "", link: "", logo: "", visible: false };
const inputCls = "w-full border border-ink/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber";

export default function Panels() {
  const [panels, setPanels] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    apiFetch("/admin/panels").then(setPanels).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    await apiFetch("/admin/panels", { method: "POST", body: JSON.stringify(form) });
    toast.success("Panel added");
    setForm(EMPTY);
    load();
  };

  const update = async (panel, patch) => {
    await apiFetch(`/admin/panels/${panel.id}`, { method: "PUT", body: JSON.stringify({ ...panel, ...patch }) });
    load();
  };

  const remove = async (id) => {
    await apiFetch(`/admin/panels/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div data-testid="admin-panels">
      <Toaster position="top-center" richColors />
      <p className="eyebrow">Panels</p>
      <h1 className="mt-2 font-serif text-3xl tracking-tight text-ink">Panel & healthcare partners</h1>
      <p className="mt-3 max-w-xl text-sm text-ink/60">
        Only panels marked <span className="font-semibold">approved</span> and{" "}
        <span className="font-semibold">visible</span> appear on the public Dental site.
      </p>

      <div className="mt-8 space-y-3">
        {panels.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-4 border border-ink/10 bg-warmwhite p-5" data-testid={`admin-panel-${p.name.toLowerCase()}`}>
            <p className="w-32 font-medium text-ink">{p.name}</p>
            <select
              value={p.status}
              onChange={(e) => update(p, { status: e.target.value })}
              data-testid={`panel-status-${p.name.toLowerCase()}`}
              className="border border-ink/15 bg-white px-2 py-1.5 text-xs"
            >
              <option value="approved">Approved</option>
              <option value="applying">Applying</option>
            </select>
            <label className="flex items-center gap-2 text-xs text-ink/70">
              <input
                type="checkbox"
                checked={p.visible}
                onChange={(e) => update(p, { visible: e.target.checked })}
                data-testid={`panel-visible-${p.name.toLowerCase()}`}
              />
              Show publicly
            </label>
            <button onClick={() => remove(p.id)} className="ml-auto text-ink/40 hover:text-red-600" aria-label={`Delete ${p.name}`} data-testid={`panel-delete-${p.name.toLowerCase()}`}>
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={add} className="mt-10 grid gap-4 border border-ink/10 bg-warmwhite p-6 sm:grid-cols-2" data-testid="panel-add-form">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50 sm:col-span-2">Add panel</p>
        <input required placeholder="Panel name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="panel-name-input" />
        <input placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="panel-desc-input" />
        <input placeholder="External link (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className={inputCls} data-testid="panel-link-input" />
        <input placeholder="Logo URL (optional)" value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} className={inputCls} data-testid="panel-logo-input" />
        <div className="flex items-center gap-6 sm:col-span-2">
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="border border-ink/15 bg-white px-2 py-2 text-xs" data-testid="panel-status-input">
            <option value="applying">Applying</option>
            <option value="approved">Approved</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-ink/70">
            <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="panel-visible-input" />
            Show publicly
          </label>
          <button type="submit" className="btn-primary ml-auto !px-5 !py-2.5" data-testid="panel-add-submit">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Add
          </button>
        </div>
      </form>
    </div>
  );
}
